import { JSONSchema7, JSONSchema7Definition } from 'json-schema';
import { DEPENDENCY_MANAGER_ID_SEPARATOR } from '../utils';

/**
 * Extracts a canonical field path from RJSF's ID format
 *
 * RJSF uses:
 * - Single underscore `_` as property separator (e.g. root_parent_child = formData.parent.child)
 * - Numeric segments as array indices (e.g. root_items_0_prop = formData.items[0].prop)
 *
 * IMPORTANT: RJSF does NOT encode underscores in property names. A property named "expectation_type"
 * becomes "root_expectation_type" in the ID, which is indistinguishable from nested properties
 * "expectation.type" without the schema context.
 *
 * This function converts RJSF's ID to a canonical field path for the dependency manager:
 * - root_parent_child → parent>child (nested properties)
 * - root_items_0_prop → items>0>prop (array items with indices)
 * - root_expectation_type → expectation_type (when schema has property "expectation_type")
 *
 * @param id - The RJSF ID
 * @param rootSchema - Optional root schema to validate property names
 * @returns The canonical field path
 */

export function extractFieldPathFromRjsfId(
  id: string,
  rootSchema?: JSONSchema7,
): string {
  if (!id) return '';

  // Remove 'root_' prefix
  const remaining = id.replace(/^root_/, '');

  if (!remaining) return '';

  // If we have a schema, use schema-aware parsing
  if (rootSchema) {
    return parseWithSchema(remaining, rootSchema);
  }

  // Fallback: simple underscore splitting (may break on property names with underscores)
  return parseWithoutSchema(remaining);
}

/**
 * Collect all properties from a schema, including those inside allOf/anyOf/oneOf and if/then/else
 * This handles conditional schemas where properties are defined in different branches
 */
function collectAllProperties(schema: JSONSchema7): {
  [key: string]: JSONSchema7Definition;
} {
  const allProperties: { [key: string]: JSONSchema7Definition } = {};

  // Direct properties
  if (schema.properties) {
    Object.assign(allProperties, schema.properties);
  }

  // Properties from allOf
  if (schema.allOf) {
    for (const subSchema of schema.allOf) {
      if (typeof subSchema !== 'boolean') {
        Object.assign(allProperties, collectAllProperties(subSchema));
      }
    }
  }

  // Properties from anyOf
  if (schema.anyOf) {
    for (const subSchema of schema.anyOf) {
      if (typeof subSchema !== 'boolean') {
        Object.assign(allProperties, collectAllProperties(subSchema));
      }
    }
  }

  // Properties from oneOf
  if (schema.oneOf) {
    for (const subSchema of schema.oneOf) {
      if (typeof subSchema !== 'boolean') {
        Object.assign(allProperties, collectAllProperties(subSchema));
      }
    }
  }

  // Properties from if/then/else
  if (schema.then && typeof schema.then !== 'boolean') {
    Object.assign(allProperties, collectAllProperties(schema.then));
  }
  if (schema.else && typeof schema.else !== 'boolean') {
    Object.assign(allProperties, collectAllProperties(schema.else));
  }

  // Properties from dependencies (schema form: when a field is present, additional
  // properties become available via oneOf/anyOf branches or direct properties).
  // This handles patterns like:
  //   dependencies: { typeQuality: { oneOf: [{ properties: { propertyType: {...} } }] } }
  if (schema.dependencies) {
    for (const dep of Object.values(schema.dependencies)) {
      if (typeof dep !== 'boolean' && !Array.isArray(dep)) {
        Object.assign(allProperties, collectAllProperties(dep as JSONSchema7));
      }
    }
  }

  return allProperties;
}

/**
 * Find a property schema by name, searching through all conditional branches
 */
function findPropertySchema(
  schema: JSONSchema7,
  propertyName: string,
): JSONSchema7 | undefined {
  const allProperties = collectAllProperties(schema);
  const propSchema = allProperties[propertyName];
  if (propSchema && typeof propSchema !== 'boolean') {
    return propSchema as JSONSchema7;
  }
  return undefined;
}

/**
 * Parse RJSF ID using schema to correctly identify property boundaries
 * Uses greedy matching: tries to find the longest property name that matches
 */
function parseWithSchema(remaining: string, rootSchema: JSONSchema7): string {
  const segments: string[] = [];
  const parts = remaining.split('_');
  let currentSchema: JSONSchema7 | undefined = rootSchema;
  let i = 0;

  while (i < parts.length && currentSchema) {
    const part = parts[i];

    // Check if this is an array index (numeric)
    if (/^\d+$/.test(part)) {
      segments.push(part);
      // Stay at array items schema
      if (currentSchema.items && typeof currentSchema.items !== 'boolean') {
        currentSchema = currentSchema.items as JSONSchema7;
      }
      i++;
      continue;
    }

    // Collect all properties including those from allOf/anyOf/oneOf and if/then/else
    const properties = collectAllProperties(currentSchema);
    let matched = false;

    // Try increasingly longer combinations starting from current position
    for (let len = parts.length - i; len >= 1; len--) {
      const candidateParts = parts.slice(i, i + len);
      const candidateName = candidateParts.join('_');

      if (properties[candidateName]) {
        segments.push(candidateName);
        const propSchema = properties[candidateName];
        currentSchema =
          typeof propSchema !== 'boolean'
            ? (propSchema as JSONSchema7)
            : undefined;
        i += len;
        matched = true;
        break;
      }
    }

    // If no match found, fall back to single segment
    if (!matched) {
      segments.push(part);
      // Try to navigate into nested schema anyway using deep search
      if (currentSchema) {
        const nestedSchema = findPropertySchema(currentSchema, part);
        currentSchema = nestedSchema;
      }
      i++;
    }
  }

  const result = segments.join(DEPENDENCY_MANAGER_ID_SEPARATOR);
  return result;
}

/**
 * Fallback parsing without schema - simple underscore splitting
 * WARNING: This will incorrectly split property names that contain underscores
 */
function parseWithoutSchema(remaining: string): string {
  // Preserve double underscores by replacing them with a marker
  const MARKER = '~~DU~~';
  const normalizedRemaining = remaining.replace(/__/g, MARKER);

  // Split by single underscore
  const segments = normalizedRemaining.split('_');

  // Restore double underscores and filter empty segments
  const fieldSegments: string[] = [];
  for (const seg of segments) {
    const restored = seg.replace(
      new RegExp(MARKER.replace(/~/g, '\\~'), 'g'),
      '__',
    );
    if (!restored) continue;
    fieldSegments.push(restored);
  }

  return fieldSegments.join(DEPENDENCY_MANAGER_ID_SEPARATOR);
}
